import React, { useEffect, useState, useRef } from 'react';
import { HotTable } from '@handsontable/react';
import 'handsontable/dist/handsontable.full.min.css';
import { db } from '../firebaseConfig';
import { doc, setDoc, getDocs, collection, deleteDoc } from 'firebase/firestore';

export const COMPANY_INFO_HEADERS = [
  '거래처상호', '사업자번호', '주소', '연락처', '대표자', '소속', '비고', '메모',
] as const;

export const COMPANY_INFO_FIELDS = [
  'company', 'businessNumber', 'juso', 'contact', 'ceo', 'department', 'remarks', 'memo',
] as const;

export const DISPATCH_COMPANY_COLLECTION = 'dispatch_company';
export const CONTAINER_CARRIER_COLLECTION = 'container_carrier_company';

const MIN_ROWS = 10;

interface Props {
  collectionName: string;
}

const CompanyInfoGrid: React.FC<Props> = ({ collectionName }) => {
  const [tableData, setTableData] = useState<any[][]>([['']]);
  const hotRef = useRef<any>(null);

  useEffect(() => {
    const fetchCompanies = async () => {
      const colRef = collection(db, collectionName);
      const querySnap = await getDocs(colRef);
      const companies: Record<string, string>[] = [];
      querySnap.forEach(docSnap => {
        const data = docSnap.data();
        if (Object.values(data).some(v => v && String(v).trim() !== '')) {
          companies.push(data as Record<string, string>);
        }
      });
      const rows = companies.map(c => COMPANY_INFO_FIELDS.map(f => c[f] || ''));
      while (rows.length < MIN_ROWS) {
        rows.push(Array(COMPANY_INFO_HEADERS.length).fill(''));
      }
      setTableData(rows);
    };
    void fetchCompanies();
  }, [collectionName]);

  const saveCompany = async (companyObj: Record<string, string>) => {
    if (!companyObj.company) return;
    await setDoc(doc(db, collectionName, companyObj.company), companyObj);
  };

  const handleAfterChange = async (changes: any, source: string) => {
    if (source === 'loadData' || !changes) return;
    const newTable = hotRef.current?.hotInstance?.getData() || tableData;
    for (const [row] of changes) {
      const company = String(newTable[row][0] || '').trim();
      if (!company) continue;
      const companyObj: Record<string, string> = {};
      COMPANY_INFO_FIELDS.forEach((f, idx) => {
        companyObj[f] = newTable[row][idx] || '';
      });
      await saveCompany(companyObj);
    }
  };

  const handleAddRow = () => {
    setTableData(prev => [...prev, Array(COMPANY_INFO_HEADERS.length).fill('')]);
    setTimeout(() => {
      if (hotRef.current) {
        const row = tableData.length;
        hotRef.current.hotInstance.selectCell(row, 0);
      }
    }, 100);
  };

  const handleDeleteRow = async (rowIndex: number) => {
    const row = tableData[rowIndex];
    const company = row[0];
    if (company) {
      await deleteDoc(doc(db, collectionName, String(company)));
    }
    setTableData(prev => prev.filter((_, idx) => idx !== rowIndex));
  };

  return (
    <div
      style={{
        flex: '1 1 0',
        minHeight: 0,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-start',
        overflow: 'hidden',
        width: '100%',
      }}
    >
      <div style={{ flex: '0 0 auto', width: '100%' }}>
        <p style={{ color: '#888', fontSize: 14, marginBottom: 8, marginTop: 0 }}>
          엑셀/구글시트에서 회사명 목록을 복사해 <b>셀을 한 번 클릭(커서 없이 파란 테두리만 보이는 상태)</b> 후 <b>Ctrl+V</b>로 붙여넣기 하세요.<br />
          셀을 더블클릭(커서 깜빡임) 상태에서는 여러 행/열 붙여넣기가 안 됩니다.
        </p>
        <button
          type="button"
          onClick={handleAddRow}
          style={{
            display: 'inline-block',
            marginBottom: 12,
            padding: '6px 18px',
            borderRadius: 4,
            border: 'none',
            background: '#1976d2',
            color: '#fff',
            fontWeight: 600,
            cursor: 'pointer',
          }}
        >
          + 추가
        </button>
      </div>

      <div style={{
        flex: '1 1 0',
        minHeight: 0,
        overflow: 'auto',
        border: '1px solid #e5e7eb',
        borderRadius: 8,
        width: '100%',
        alignSelf: 'stretch',
      }}
      >
        <HotTable
          ref={hotRef}
          data={tableData}
          colHeaders={[...COMPANY_INFO_HEADERS]}
          rowHeaders
          width="100%"
          height="100%"
          licenseKey={process.env.REACT_APP_HANDSONTABLE_LICENSE_KEY || 'non-commercial-and-evaluation'}
          manualRowResize
          manualColumnResize
          autoColumnSize
          autoRowSize={false}
          className="htCenter"
          afterChange={handleAfterChange}
          readOnly={false}
          contextMenu={{
            items: {
              remove_row: {
                name: '삭제',
                callback(_key: string, selection: any) {
                  const rowIndex = selection[0].start.row;
                  void handleDeleteRow(rowIndex);
                },
              },
            },
          }}
          copyPaste
          stretchH="all"
        />
      </div>
    </div>
  );
};

export default CompanyInfoGrid;
